import React, { useRef, useState, useEffect } from "react";
import { io, Socket } from "socket.io-client";
import { PanelRightOpen, PanelRightClose, Mic, MicOff } from "lucide-react";
import SphereParticles from "../components/blob";
import Orb from "../components/orb";
import LiquidChrome from "../components/LiquidChrome";
import { useNavigate } from "react-router-dom";
import { createScreenTracker } from "../utils/screenTracking";
import { createProctoringTracker } from "../utils/proctoringTracker";
import { createAudioAnalyzer } from "../utils/audioAnalysis";

const InterviewPage = ({sessionId, cameraStream, screenStream, onInterviewEnd}: {sessionId: string | null, cameraStream: MediaStream | null, screenStream: MediaStream | null, onInterviewEnd?: () => void}) => {
  const socketRef = useRef<Socket | null>(null);
  const navigate = useNavigate();
  const [connected, setConnected] = useState(false);
  const [amplitude, setAmplitude] = useState(0);
  const [aiText, setAiText] = useState<string | null>(" ");
  const [userText, setUserText] = useState(" ");
  const [showInterviewEndedPopup, setShowInterviewEndedPopup] = useState(false);
  const [userOpinion, setUserOpinion] = useState("");
  const audioContextRef = useRef<AudioContext | null>(null);
  const [timeleft, setTimeleft] = useState(0);
  const [countdownFinished, setCountdownFinished] = useState(false);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const cameraVideoRef = useRef<HTMLVideoElement | null>(null);
  const amplitudeThrottleRef = useRef<number>(0);
  const [previewStream, setPreviewStream] = useState<MediaStream | null>(null);
  const playedByGestureRef = useRef(false);
  const isAiPlayingRef = useRef(false);
  const [micActive, setMicActive] = useState(false);
  const [previewStarted, setPreviewStarted] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const orbWrapRef = useRef<HTMLDivElement | null>(null);
  const prevCountdownFinishedRef = useRef(false);
  const [recordingElapsedSeconds, setRecordingElapsedSeconds] = useState(0);
  const [recordingStartTime, setRecordingStartTime] = useState<number | null>(null);
  const [transcriptPanelOpen, setTranscriptPanelOpen] = useState(false);
  const [transcriptLog, setTranscriptLog] = useState<{ role: "ai" | "user"; text: string }[]>([]);
  const transcriptEndRef = useRef<HTMLDivElement | null>(null);
  const nextAiTextIsNewSegmentRef = useRef(true);
  const [showLeaveWarning, setShowLeaveWarning] = useState(false);
  const hasLeftTabRef = useRef(false);
  const [showDontRefreshBanner, setShowDontRefreshBanner] = useState(false);
  const screenshotTrackerRef = useRef<ReturnType<typeof createScreenTracker> | null>(null);
  const screenshotIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const proctoringTrackerRef = useRef<ReturnType<typeof createProctoringTracker> | null>(null);
  const audioAnalyzerRef = useRef<ReturnType<typeof createAudioAnalyzer> | null>(null);

  // Warn when candidate switches tab, refreshes, or tries to close (interview active)
  const interviewActive = connected && !showInterviewEndedPopup;
  useEffect(() => {
    if (!interviewActive) {
      setShowDontRefreshBanner(false);
      return;
    }
    setShowDontRefreshBanner(true);
    const t = setTimeout(() => setShowDontRefreshBanner(false), 4000);
    return () => clearTimeout(t);
  }, [interviewActive]);

  useEffect(() => {
    if (!interviewActive) return;
    const onVisibilityChange = () => {
      if (document.hidden) {
        hasLeftTabRef.current = true;
      } else {
        if (hasLeftTabRef.current) {
          setShowLeaveWarning(true);
          hasLeftTabRef.current = false;
        }
      }
    };
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "Leaving may end or disrupt your interview. Are you sure you want to leave?";
      return e.returnValue;
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [interviewActive]);

  // Cleanup proctoring/screenshot tracking on unmount
  useEffect(() => {
    return () => {
      if (screenshotIntervalRef.current) {
        clearInterval(screenshotIntervalRef.current);
        screenshotIntervalRef.current = null;
      }
      screenshotTrackerRef.current?.stopFaceCapture();
      screenshotTrackerRef.current = null;

      proctoringTrackerRef.current?.stop();
      proctoringTrackerRef.current = null;

      audioAnalyzerRef.current?.stop();
      audioAnalyzerRef.current = null;
    };
  }, []);

  useEffect(() => {
    const prev = prevCountdownFinishedRef.current;
    prevCountdownFinishedRef.current = countdownFinished;
    if (prev || !countdownFinished) return;

    // Pop animation when countdown completes
    const el = orbWrapRef.current;
    if (!el) return;
    el.animate(
      [
        { transform: 'scale(0.82)', opacity: 0.0, filter: 'blur(4px)' },
        { transform: 'scale(1.06)', opacity: 1.0, filter: 'blur(0px)' },
        { transform: 'scale(1.0)', opacity: 1.0, filter: 'blur(0px)' },
      ],
      { duration: 520, easing: 'cubic-bezier(0.2, 0.9, 0.2, 1.0)', fill: 'both' }
    );
  }, [countdownFinished]);

  // Always use our own camera stream for preview so we have full control (parent's stream can be stale)
  useEffect(() => {
    let stream: MediaStream | null = null;
    navigator.mediaDevices.getUserMedia({ video: true, audio: false })
      .then((s) => {
        stream = s;
        setPreviewStream(s);
      })
      .catch((err) => console.error("Could not get camera for preview:", err));
    return () => {
      stream?.getTracks().forEach((t) => t.stop());
      setPreviewStream(null);
    };
  }, []);

  // Prefer our own preview stream for display; fall back to parent's only if we don't have ours yet
  const displayStream = previewStream ?? cameraStream;

  const attachStreamToVideo = (video: HTMLVideoElement | null, stream: MediaStream | null) => {
    if (!video || !stream) return;
    const videoTracks = stream.getVideoTracks();
    if (!videoTracks.length) return;
    videoTracks.forEach((t) => (t.enabled = true));
    video.srcObject = stream;
    video.load();
    const doPlay = () => {
      video.play().then(() => setPreviewStarted(true)).catch(() => {});
    };
    video.onloadedmetadata = doPlay;
    video.onloadeddata = doPlay;
    video.onplaying = () => {
      video.onloadedmetadata = null;
      video.onloadeddata = null;
      video.onplaying = null;
      setPreviewStarted(true);
    };
    doPlay();
    window.setTimeout(doPlay, 200);
  };

  // Only set the ref here - never attach in ref callback (would run every re-render and cause flicker when amplitude updates)
  const setCameraVideoRef = (el: HTMLVideoElement | null) => {
    (cameraVideoRef as React.MutableRefObject<HTMLVideoElement | null>).current = el;
  };
  useEffect(() => {
    if (cameraVideoRef.current && displayStream) {
      attachStreamToVideo(cameraVideoRef.current, displayStream);
    }
    // Track camera stream for proctoring (camera/mic stopped events)
    if (displayStream && sessionId && proctoringTrackerRef.current) {
      proctoringTrackerRef.current.trackMediaStream(displayStream, { camera: true, microphone: false });
    }
  }, [displayStream, transcriptPanelOpen]);

  // Start camera preview on user gesture (browsers block video.play() until then)
  const handleStartPreview = async () => {
    if (playedByGestureRef.current) return;
    let stream = displayStream;
    if (!stream) {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
        setPreviewStream(stream);
      } catch (e) {
        console.error("Could not get camera:", e);
        return;
      }
    }
    if (!stream) return;
    playedByGestureRef.current = true;
    const video = cameraVideoRef.current;
    if (video) {
      attachStreamToVideo(video, stream);
      video.play().then(() => setPreviewStarted(true)).catch(() => {
        playedByGestureRef.current = false;
      });
    }
  };

  useEffect(() => {
    if (timeleft <= 0) return;

    const timer = setInterval(() => {
      setTimeleft((prev) => {
        const next = Math.max(0, prev - 1);
        if (next === 0) setCountdownFinished(true); // Sphere pops up only after countdown ends
        return next;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [timeleft]);

  // Recording timer: update every second from interview start
  useEffect(() => {
    if (recordingStartTime == null || showInterviewEndedPopup) return;
    const interval = setInterval(() => {
      setRecordingElapsedSeconds(Math.floor((Date.now() - recordingStartTime) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [recordingStartTime, showInterviewEndedPopup]);

  // Auto-scroll transcript panel to latest
  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [transcriptLog]);


const convertFloatTo16BitPCM = (input: Float32Array) => {
  const buffer = new ArrayBuffer(input.length * 2);
  const view = new DataView(buffer);

  let offset = 0;
  for (let i = 0; i < input.length; i++, offset += 2) {
    const s = Math.max(-1, Math.min(1, input[i]));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }

  return buffer;
};

const bufferToBase64 = (buffer: ArrayBuffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};


function downsampleBuffer(buffer: Float32Array, inputRate: number, outputRate: number) {  
  if (inputRate === outputRate) return buffer;

  const ratio = inputRate / outputRate;
  const newLength = Math.round(buffer.length / ratio);
  const result = new Float32Array(newLength);

  let offset = 0;

  for (let i = 0; i < newLength; i++) {
    const start = Math.round(i * ratio);
    const end = Math.round((i + 1) * ratio);
    let sum = 0;
    let count = 0;

    for (let j = start; j < end && j < buffer.length; j++) {
      sum += buffer[j];
      count++;
    }

    result[i] = sum / count;
  }

  return result;
}

const startMic = async () => {
  console.log("Starting microphone");
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: true,
  });

  const audioContext = new AudioContext({ sampleRate: 24000 });
  console.log("Actual sample rate:", audioContext.sampleRate,"<==========");

  const source = audioContext.createMediaStreamSource(stream);
  const processor = audioContext.createScriptProcessor(4096, 1, 1);
  processorRef.current = processor;

  source.connect(processor);
  processor.connect(audioContext.destination);

  const silentBuffer = bufferToBase64(convertFloatTo16BitPCM(new Float32Array(4096)));

  // Simple client-side VAD based on mic amplitude
  const SPEECH_THRESHOLD = 0.04; // tune experimentally
  const SPEECH_END_MS = 800;     // how long below threshold = stopped
  const userSpeakingRef = { current: false };
  const lastSpeechAtRef = { current: 0 };

  processor.onaudioprocess = (event) => {
    const input = event.inputBuffer.getChannelData(0);
    const pcm = convertFloatTo16BitPCM(input);
    const base64 = isAiPlayingRef.current ? silentBuffer : bufferToBase64(pcm);
    socketRef.current?.emit("audio-stt", base64);
    socketRef.current?.emit("audio-stream", base64);

    // --- Detect when the candidate is actually speaking (vs background noise) ---
    // Compute RMS on the mic input
    let sum = 0;
    for (let i = 0; i < input.length; i++) {
      const v = input[i];
      sum += v * v;
    }
    const rms = Math.sqrt(sum / input.length);
    const now = Date.now();

    if (rms > SPEECH_THRESHOLD && !isAiPlayingRef.current) {
      // Consider this as active speech
      lastSpeechAtRef.current = now;
      if (!userSpeakingRef.current) {
        userSpeakingRef.current = true;
        if (sessionId && socketRef.current) {
          socketRef.current.emit("user-speaking", { sessionId, rms });
        }
      }
    } else {
      // Potentially ended speaking
      if (userSpeakingRef.current && now - lastSpeechAtRef.current > SPEECH_END_MS) {
        userSpeakingRef.current = false;
        if (sessionId && socketRef.current) {
          socketRef.current.emit("user-stopped-speaking", { sessionId });
        }
      }
    }
  };
};

  const startScreenshotLoop = () => {
    if (!sessionId || !cameraVideoRef.current) return;

    if (!screenshotTrackerRef.current) {
      screenshotTrackerRef.current = createScreenTracker(sessionId, {
        videoElement: cameraVideoRef.current,
      });
    }

    if (screenshotIntervalRef.current) {
      clearInterval(screenshotIntervalRef.current);
    }

    screenshotIntervalRef.current = setInterval(() => {
      if (cameraVideoRef.current && screenshotTrackerRef.current) {
        screenshotTrackerRef.current.startFaceCapture(cameraVideoRef.current);
      }
    }, 3000);
  };

  const startInterview = () => {



    const socket = io(process.env.REACT_APP_SOCKET_URL || "http://localhost:3001", {
      transports: ["websocket"],
    });

    socketRef.current = socket;

    socket.on("connect", () => {
      console.log("Connected to backend");
      setConnected(true);
      socket.emit("start-interview", {
        sessionId: sessionId,
      });
    });





    socket.on("interview-ready", () => {
      console.log("Interview ready");
      const now = Date.now();
      setRecordingStartTime(now);
      setRecordingElapsedSeconds(0);
      startMic();
      startRecording();
      startScreenshotLoop();

      // Start behavioural proctoring tracker for this session
      if (sessionId && !proctoringTrackerRef.current) {
        proctoringTrackerRef.current = createProctoringTracker(sessionId, {});
        proctoringTrackerRef.current.start();
        if (displayStream) {
          proctoringTrackerRef.current.trackMediaStream(displayStream, { camera: true, microphone: false });
        }
      }
    });

    socket.on("ai-audio", (data) => {
      // Backend sends { audio: base64String }
      const base64 = typeof data === 'string' ? data : data?.audio;
      if (base64) playAudio(base64);
    });

    socket.on("ai-text", (data) => {
      const chunk = typeof data === "string" ? data : (data?.text ?? "");
      if (!chunk) return;
      const startNewSegment = nextAiTextIsNewSegmentRef.current;
      if (startNewSegment) {
        nextAiTextIsNewSegmentRef.current = false;
        setAiText(chunk);
      } else {
        setAiText((prev) => prev + chunk);
      }
      setTranscriptLog((prev) => {
        const last = prev[prev.length - 1];
        if (startNewSegment) return [...prev, { role: "ai", text: chunk }];
        if (last?.role === "ai") return [...prev.slice(0, -1), { role: "ai", text: last.text + chunk }];
        return [...prev, { role: "ai", text: chunk }];
      });
    });

    // Backend sends when AI turn is complete; next AI text will be a new segment in the transcript
    socket.on("ai-turn-done", (data) => {
      const text = typeof data === "string" ? data : (data?.text ?? "");
      if (text.trim() && sessionId) {
        socket.emit("add-interaction", { sessionId, role: "ai", text: text.trim() });
      }
      nextAiTextIsNewSegmentRef.current = true;
    });

    socket.on("candidate-transcript", (data) => {
      const text = data?.text ?? "";
      if (!text.trim()) return;
      setUserText(text);
      if (data?.isFinal === true) {
        setTranscriptLog((prev) => [...prev, { role: "user", text: text.trim() }]);
        if (sessionId && data?.isFinal === true) socket.emit("add-interaction", { sessionId, role: "user", text: text.trim() });
      }

      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      const isFinal = data?.isFinal === true;
      timeoutRef.current = setTimeout(() => {
        setUserText("");
      }, isFinal ? 4000 : 2000);
    });


    socket.on("user-text", (data) => {
      const text = typeof data === "string" ? data : (data?.text ?? "");
      console.log("User text received", text);
      if (text) setUserText(text);
      else setUserText((prev) => prev || "…");
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => setUserText(""), 3000);
    });
    socket.on("disconnect", () => {
      console.log("Disconnected");
      setConnected(false);
    });

    socket.on("interview-ended", () => {
      console.log("Interview ended");
      stopInterview(true);
    });

    socket.on("connect_error", (err) => {
      console.log("Socket error:", err);
      console.log("Socket error:", err.message);
    });
  };

  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunkIndexRef = useRef(0);
  const micStreamRef = useRef<MediaStream | null>(null);
  const fallbackCameraRef = useRef<MediaStream | null>(null);
  const mixAudioContextRef = useRef<AudioContext | null>(null);

  /** Mix mic + system audio into single track (MediaRecorder only records 1st audio track) */
  const mixAudioForRecording = async (micStream: MediaStream, systemStream: MediaStream | null): Promise<MediaStream> => {
    const ctx = new AudioContext({ sampleRate: 48000 });
    mixAudioContextRef.current = ctx;
    if (ctx.state === "suspended") await ctx.resume();

    const dest = ctx.createMediaStreamDestination();
    const gainMic = ctx.createGain();
    const gainSystem = ctx.createGain();
    gainMic.gain.value = 1;
    gainSystem.gain.value = 1;

    const micSource = ctx.createMediaStreamSource(micStream);
    micSource.connect(gainMic);
    gainMic.connect(dest);

    const systemTracks = systemStream?.getAudioTracks() ?? [];
    if (systemTracks.length > 0) {
      const systemStreamOnly = new MediaStream(systemTracks);
      const systemSource = ctx.createMediaStreamSource(systemStreamOnly);
      systemSource.connect(gainSystem);
      gainSystem.connect(dest);
    }

    return dest.stream;
  };

  const startRecording = async () => {
    console.log("Starting recording (camera + mic + system audio)");
    let camStream: MediaStream | null = cameraStream;
    const camVideoTracks = camStream?.getVideoTracks() ?? [];
    const hasLiveVideo = camVideoTracks.some((t) => t.readyState === "live");
    if (!hasLiveVideo) {
      // Use our preview stream if live, else request new camera
      const preview = previewStream ?? null;
      const previewLive = preview?.getVideoTracks().some((t) => t.readyState === "live");
      if (previewLive && preview) {
        camStream = preview;
      } else {
        try {
          camStream = await navigator.mediaDevices.getUserMedia({ video: true });
          fallbackCameraRef.current = camStream;
        } catch (e) {
          console.error("No camera stream:", e);
          return;
        }
      }
    }
    if (!camStream) return;

    const mic = await navigator.mediaDevices.getUserMedia({ audio: true });
    micStreamRef.current = mic;

    // Start audio analyzer for proctoring (audio_silent / audio_loud flags)
    if (sessionId) {
      if (!audioAnalyzerRef.current) {
        audioAnalyzerRef.current = createAudioAnalyzer(sessionId, {});
      }
      try {
        await audioAnalyzerRef.current.start(mic);
      } catch (e) {
        console.error("Audio analyzer start failed:", e);
      }
    }

    // MediaRecorder only records first audio track - mix mic + system into one
    const mixedAudio = await mixAudioForRecording(mic, screenStream);
    const combinedStream = new MediaStream([
      ...camStream.getVideoTracks(),
      ...mixedAudio.getAudioTracks(),
    ]);

    const recVideoTracks = combinedStream.getVideoTracks();
    const audioTracks = combinedStream.getAudioTracks();
    console.log("Recording tracks:", { video: recVideoTracks.length, audio: audioTracks.length, systemAudioUsed: !!(screenStream?.getAudioTracks().length) });

    const mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus")
      ? "video/webm;codecs=vp9,opus"
      : MediaRecorder.isTypeSupported("video/webm;codecs=vp8,opus")
        ? "video/webm;codecs=vp8,opus"
        : "video/webm";
    const recorder = new MediaRecorder(combinedStream, {
      mimeType,
      videoBitsPerSecond: 2500000,
      audioBitsPerSecond: 128000,
    });

    recorderRef.current = recorder;
    chunkIndexRef.current = 0;

    recorder.ondataavailable = async (event) => {
      if (event.data.size > 0) {
        const buffer = await event.data.arrayBuffer();
        const idx = chunkIndexRef.current++;
        socketRef.current?.emit("video-chunk", { chunkIndex: idx, data: buffer });
      }
    };

    recorder.start(1000); // continuous chunks every 1 second
  };

  const stopInterview = async (showPopup = false) => {
    // Stop proctoring trackers tied to this session
    if (screenshotIntervalRef.current) {
      clearInterval(screenshotIntervalRef.current);
      screenshotIntervalRef.current = null;
    }
    screenshotTrackerRef.current?.stopFaceCapture();
    screenshotTrackerRef.current = null;

    proctoringTrackerRef.current?.stop();
    proctoringTrackerRef.current = null;

    audioAnalyzerRef.current?.stop();
    audioAnalyzerRef.current = null;

    const recorder = recorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      recorder.stop();
      await new Promise<void>((resolve) => {
        recorder.onstop = () => resolve();
        setTimeout(resolve, 500);
      });
    }
    micStreamRef.current?.getTracks().forEach((t) => t.stop());
    micStreamRef.current = null;
    fallbackCameraRef.current?.getTracks().forEach((t) => t.stop());
    fallbackCameraRef.current = null;
    mixAudioContextRef.current?.close();
    mixAudioContextRef.current = null;
    audioContextRef.current?.close();
    audioContextRef.current = null;
    socketRef.current?.disconnect();
    setConnected(false);
    if (showPopup) {
      setShowInterviewEndedPopup(true);
    } else {
      onInterviewEnd?.();
    }
  };

  const handleOpinionSubmit = () => {
    // TODO: send userOpinion to backend (e.g. PATCH session feedback)
    console.log("User opinion:", userOpinion);
    socketRef.current?.emit("user-feedback", {
      sessionId: sessionId,
      feedback: userOpinion,
    });
    setShowInterviewEndedPopup(false);
    setUserOpinion("");
    onInterviewEnd?.();
    window.open(process.env.REACT_APP_ASSESSMENT_URL || "https://earlyjobs.ai", "_blank", "noopener,noreferrer");
  };

  const closeInterviewEndedPopup = () => {
    setShowInterviewEndedPopup(false);
    setUserOpinion("");
    onInterviewEnd?.();
    window.open(process.env.REACT_APP_ASSESSMENT_URL || "https://earlyjobs.ai", "_blank", "noopener,noreferrer");

  };

  const startedRef = useRef(false);
  const socketStartedRef = useRef(false);
  // Start countdown when page loads (user already clicked Start from parent)
  useEffect(() => {
    if (sessionId && !startedRef.current) {
      startedRef.current = true;
      setTimeleft(3);
    }
    return () => {
      socketRef.current?.disconnect();
    };
  }, [sessionId]);

  // Only connect socket and emit start-interview when countdown hits zero
  useEffect(() => {
    if (countdownFinished && sessionId && !socketStartedRef.current) {
      socketStartedRef.current = true;
      startInterview();
    }
  }, [countdownFinished, sessionId]);
  let nextPlayTime = 0;
  let analyser: AnalyserNode | null = null;
  let dataArray: Uint8Array;
  

  let activeSources = 0;

  function playAudio(base64: string) {
    const AudioCtx = window.AudioContext as typeof AudioContext;
  
    const audioContext =
      audioContextRef.current ||
      new AudioCtx({ sampleRate: 24000 });
  
    audioContextRef.current = audioContext;
  
    // 🔥 Create analyser once
    if (!analyser) {
      analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      dataArray = new Uint8Array(analyser.frequencyBinCount);
      analyser.connect(audioContext.destination);
  
      startAmplitudeLoop(); // start visual loop once
    }
  
    const binary = atob(base64);
    const length = binary.length;
    const frameCount = length / 2;
  
    const buffer = audioContext.createBuffer(1, frameCount, 24000);
    const channelData = buffer.getChannelData(0);
  
    let offset = 0;
  
    for (let i = 0; i < length; i += 2) {
      let sample =
        binary.charCodeAt(i) |
        (binary.charCodeAt(i + 1) << 8);
  
      if (sample >= 0x8000) sample -= 0x10000;
  
      channelData[offset++] = sample / 32768;
    }
  
    const source = audioContext.createBufferSource();
    source.buffer = buffer;
  
    source.connect(analyser); // 🔥 important
  
    if (nextPlayTime < audioContext.currentTime) {
      nextPlayTime = audioContext.currentTime;
    }
  
    source.start(nextPlayTime);
    nextPlayTime += buffer.duration;
    activeSources++;
    isAiPlayingRef.current = true;
    setMicActive(false);

    source.onended = () => {
      activeSources--;
      if (activeSources < 1) {
        isAiPlayingRef.current = false;
        setMicActive(true);
        setAiText("");
        setAmplitude(0);
        // Notify backend that AI has fully finished speaking for this turn.
        // Backend will start the silence timer for this specific session/socket.
        socketRef.current?.emit("ai-finished-speaking");
      }
    }
  }




  // function startAmplitudeLoop() {
  //   if (!analyser) return;
  //   const throttleMs = 50; // ~20 updates/sec max to avoid re-render storm and camera flicker
  //   function update() {
  //     analyser!.getByteTimeDomainData(dataArray);
  //     let sum = 0;
  //     for (let i = 0; i < dataArray.length; i++) {
  //       const v = (dataArray[i] - 128) / 128;
  //       sum += v * v;
  //     }
  //     const rms = Math.sqrt(sum / dataArray.length);
  //     const next = Math.min(rms * 2, 1);
  //     const now = Date.now();
  //     if (now - amplitudeThrottleRef.current >= throttleMs) {
  //       amplitudeThrottleRef.current = now;
  //       setAmplitude(next);
  //     }
  //     requestAnimationFrame(update);
  //   }
  //   update();
  // }
    

  function startAmplitudeLoop() {
    if (!analyser) return;
  
    const NOISE_FLOOR = 0.03;   // silence cutoff
    const SMOOTHING = 0.15;     // 0–1 (higher = smoother)
    const BOOST = 3;            // make speech stronger
  
    let smoothed = 0;
  
    function update() {
      if (!analyser) return;
  
      // TS/lib mismatch across versions: ensure it compiles consistently.
      (analyser as any).getByteTimeDomainData(dataArray);
  
      let sum = 0;
      for (let i = 0; i < dataArray.length; i++) {
        const v = (dataArray[i] - 128) / 128;
        sum += v * v;
      }
  
      const rms = Math.sqrt(sum / dataArray.length);
  
      // 🔥 noise gate
      let raw = rms < NOISE_FLOOR ? 0 : rms;
  
      // 🔥 boost speech
      raw = Math.min(raw * BOOST, 1);
  
      // 🔥 smooth
      smoothed += (raw - smoothed) * SMOOTHING;
  
      // 🔥 hard stop near silence
      if (smoothed < 0.02) smoothed = 0;
  
      setAmplitude(smoothed);
  
      // Keep loop running; cost is tiny and avoids needing to restart on later questions.
      requestAnimationFrame(update);
    }
  
    requestAnimationFrame(update);
  }
  
  

  
  return (
    <div className="min-h-screen relative flex flex-col bg-stone-100 overflow-hidden p-4 md:p-6 lg:p-8 z-100 position-relative">
      {/* Styled reminder: don't refresh or close (auto-hide after a few seconds) */}
      {showDontRefreshBanner && (
        <div className="fixed top-0 left-0 right-0 z-[1050] py-1.5 px-4 bg-stone-200/90 text-stone-600 text-center text-xs">
          Please don&apos;t refresh or close this tab during the interview.
        </div>
      )}

      {/* Warning when candidate returns after switching tab */}
      {showLeaveWarning && (
        <div className="fixed inset-0 z-[1300] flex items-center justify-center p-4 bg-black/30">
          <div className="bg-white rounded-xl shadow-lg max-w-sm w-full p-5 border border-stone-200">
            <p className="text-stone-700 text-sm leading-relaxed">
              You switched tabs. Please stay on this tab for the rest of the interview.
            </p>
            <button
              type="button"
              onClick={() => setShowLeaveWarning(false)}
              className="mt-4 w-full px-4 py-2 rounded-lg bg-stone-100 hover:bg-stone-200 text-stone-700 text-sm font-medium transition"
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* Interview ended popup */}
      {showInterviewEndedPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl border border-stone-200 max-w-md w-full p-6 space-y-4">
            <h2 className="text-xl font-semibold text-stone-800">Interview ended</h2>
            <p className="text-stone-600 text-sm">
              Thanks for completing the interview. Your feedback helps us improve.
            </p>
            <div>
              <label htmlFor="opinion" className="block text-sm font-medium text-stone-700 mb-1.5">
                Your feedback (optional)
              </label>
              <textarea
                id="opinion"
                value={userOpinion}
                onChange={(e) => setUserOpinion(e.target.value)}
                placeholder="How was your experience? Any suggestions?"
                rows={4}
                className="w-full px-3 py-2.5 rounded-lg border border-stone-300 text-stone-800 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none text-sm"
              />
            </div>
            <div className="flex gap-3 pt-1">
              <button
                type="button"
                onClick={closeInterviewEndedPopup}
                className="flex-1 px-4 py-2.5 rounded-lg border border-stone-300 text-stone-700 hover:bg-stone-50 font-medium text-sm transition"
              >
                Skip
              </button>
              <button
                type="button"
                onClick={handleOpinionSubmit}
                className="flex-1 px-4 py-2.5 rounded-lg bg-orange-500 hover:bg-orange-600 text-white font-medium text-sm transition shadow-sm"
              >
                Submit feedback
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Top-left under logo: recording timer pill */}
      {countdownFinished && connected && !showInterviewEndedPopup && recordingStartTime != null && (
        <div className="absolute left-4 top-24 z-[1100] flex items-center gap-1 px-2 py-1 rounded-full bg-red-600 text-white shadow">
          <span className="relative flex items-center justify-center w-2 h-2 rounded-full border border-white" aria-hidden>
            <span className="absolute w-0.5 h-0.5 rounded-full bg-white" />
          </span>
          <span className="font-mono font-semibold tabular-nums text-[10px] leading-tight">
            {String(Math.floor(recordingElapsedSeconds / 60)).padStart(2, "0")}:
            {String(recordingElapsedSeconds % 60).padStart(2, "0")}
          </span>
          <span className="text-[10px] font-medium leading-tight">Recording</span>
        </div>
      )}

      {/* Top-left: logo */}
      <section className="absolute top-4 left-4 z-[1100] flex items-center gap-2">
        <img
          src="https://portal.earlyjobs.ai/logo512.png"
          alt="EarlyJobs"
          className="h-16 w-16 object-contain md:h-20 md:w-20"
        />
      </section>

      {/* Right: transcript panel (collapsible) + camera preview */}
      {transcriptPanelOpen ? (
        <>
          {/* Transcript panel open: full-height panel on the right; transcript above, video at bottom */}
          <section className="fixed right-0 top-0 bottom-0 w-80 max-w-[85vw] bg-white/98 backdrop-blur border-l border-stone-200 shadow-xl z-[1100] flex flex-col">
            <div className="flex items-center justify-between shrink-0 px-3 py-2 border-b border-stone-200">
              <span className="text-sm font-semibold text-stone-700">Transcription</span>
              <button
                type="button"
                onClick={() => setTranscriptPanelOpen(false)}
                className="p-1.5 rounded-lg text-stone-500 hover:bg-stone-100 hover:text-stone-700 transition"
                aria-label="Close transcript"
              >
                <PanelRightClose className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-3 space-y-2">
              {transcriptLog.length === 0 && (
                <p className="text-stone-400 text-sm">Transcript will appear here as you speak.</p>
              )}
              {transcriptLog.map((entry, i) => (
                <div
                  key={i}
                  className={`rounded-lg px-3 py-2 text-sm ${
                    entry.role === "ai"
                      ? "bg-orange-50 border border-orange-100 text-stone-800"
                      : "bg-stone-100 border border-stone-200 text-stone-800"
                  }`}
                >
                  <span className="font-semibold text-stone-500 block mb-0.5">{entry.role === "ai" ? "SARA" : "You"}</span>
                  <p className="whitespace-pre-wrap break-words">{entry.text}</p>
                </div>
              ))}
              <div ref={transcriptEndRef} />
            </div>
            {/* Video preview: bottom of panel, below transcript */}
            <div className="shrink-0 p-2 border-t border-stone-200 bg-stone-50/50">
              <div className="relative aspect-video min-h-[100px] w-full max-w-[240px] mx-auto rounded-lg overflow-hidden bg-stone-900">
                <div className="absolute top-1.5 left-1.5 z-10 w-2 h-2 rounded-full bg-red-500 animate-ping" />
                <div className="absolute top-1.5 left-1.5 z-10 w-2 h-2 rounded-full bg-red-600" />
                <video
                  key={displayStream?.id ?? "preview"}
                  ref={setCameraVideoRef}
                  autoPlay
                  playsInline
                  muted
                  className="absolute inset-0 w-full h-full object-cover"
                  style={{ transform: "scaleX(-1)", visibility: displayStream ? "visible" : "hidden" }}
                  onPlaying={() => setPreviewStarted(true)}
                />
                {!previewStarted && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleStartPreview(); }}
                    onTouchEnd={(e) => { e.preventDefault(); e.stopPropagation(); handleStartPreview(); }}
                    className="absolute inset-0 z-10 flex items-center justify-center bg-stone-800/90 text-stone-200 text-xs text-center px-2 cursor-pointer hover:bg-stone-700/90 transition"
                  >
                    {displayStream ? "Click to show camera" : "Click to start camera"}
                  </button>
                )}
              </div>
            </div>
          </section>
        </>
      ) : (
        <>
          {/* Video preview: top-right when panel closed */}
          <section className="fixed top-4 right-4 z-[1100] w-80 max-w-[calc(100vw-2rem)] bg-white/95 backdrop-blur rounded-xl shadow-lg border border-stone-200/80 p-1 flex flex-col gap-2">
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setTranscriptPanelOpen(true)}
                className="p-2 rounded-lg text-stone-500 hover:bg-stone-100 hover:text-stone-700 transition"
                aria-label="Open transcript"
              >
                <PanelRightOpen className="w-5 h-5" />
              </button>
            </div>
            <div className="relative aspect-video min-h-[140px] bg-stone-900 rounded-lg overflow-hidden">
              <div className="relative w-3 h-3 flex items-center justify-center z-10 mt-2 ml-2">
                <span className="absolute w-3 h-3 rounded-full bg-red-500 opacity-75 animate-ping"></span>
                <span className="relative w-3 h-3 rounded-full bg-red-600"></span>
              </div>
              <video
                key={displayStream?.id ?? "preview"}
                ref={setCameraVideoRef}
                autoPlay
                playsInline
                muted
                className="absolute inset-0 w-full h-full object-cover"
                style={{ transform: "scaleX(-1)", visibility: displayStream ? "visible" : "hidden" }}
                onPlaying={() => setPreviewStarted(true)}
              />
              {!previewStarted && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleStartPreview(); }}
                  onTouchEnd={(e) => { e.preventDefault(); e.stopPropagation(); handleStartPreview(); }}
                  className="absolute inset-0 z-10 flex items-center justify-center bg-stone-800/90 text-stone-200 text-sm text-center px-3 cursor-pointer hover:bg-stone-700/90 transition"
                >
                  {displayStream ? "Click to show camera" : "Click to start camera"}
                </button>
              )}
            </div>
          </section>
        </>
      )}

      {/* Main: speaker (sphere) container with padding, does not fill full width/height */}
      <section className="flex-1 min-h-0 flex items-center justify-center rounded-xl overflow-hidden">
        {timeleft > 0 ? (
          <div className="flex flex-col items-center gap-1 rounded-2xl bg-white/90 backdrop-blur-sm px-8 py-6 shadow-lg border border-orange-200/60">
            <span className="text-sm font-medium text-stone-500 uppercase tracking-wider">Interview starts in</span>
            <span className="text-5xl font-bold tabular-nums text-orange-500 drop-shadow-sm">{timeleft}</span>
            <span className="text-xs text-stone-400">seconds</span>
          </div>
        ) : countdownFinished && !showInterviewEndedPopup ? (
          // <SphereParticles amplitude={amplitude} />
          <div
            ref={orbWrapRef}
            className="w-[min(65vw,45vh)] max-w-[380px] aspect-square rounded-full overflow-hidden bg-white shadow-2xl ring-1 ring-black/5 shadow-[0_28px_90px_rgba(249,115,22,0.28)]"
          >
            <div className="w-full h-full rounded-full shadow-inner shadow-black/10">
            <LiquidChrome
              amplitude={amplitude}
              baseColor={[0.976, 0.451, 0.086]} // orange-500-ish
              speed={0.9}
              className="w-full h-full"
            />
            </div>
          </div>
        ) : null}
      </section>

      {/* Bottom overlay: captions (only when panel closed) + End Interview button */}
      <section className="fixed inset-x-0 bottom-4 z-[1200] flex justify-center px-4 pointer-events-none">
        <div className="w-full max-w-3xl flex flex-col gap-3 items-stretch pointer-events-none">
          {!transcriptPanelOpen && !!aiText?.trim() && (
            <div className="text-black text-sm font-medium min-h-[2.5rem] line-clamp-3 bg-white/95 backdrop-blur rounded-xl shadow-lg border border-stone-200/80 p-4 pointer-events-auto">
              <span className="font-semibold">SARA</span>
              <p>{aiText}</p>
            </div>
          )}

          {!transcriptPanelOpen && !!userText?.trim() && (
            <div className="text-black text-sm font-medium min-h-[2.5rem] line-clamp-3 bg-white/95 backdrop-blur rounded-xl shadow-lg border border-stone-200/80 p-4 pointer-events-auto">
              <span className="font-semibold">You</span>
              <p>{userText}</p>
            </div>
          )}

          {connected && (
            <div className="mt-2 flex items-center justify-center gap-3 pointer-events-auto">
              {micActive ? <Mic className="w-5 h-5 text-stone-700" /> : <MicOff className="w-5 h-5 text-stone-400" />}
              <button
                onClick={() => stopInterview(true)}
                className="px-5 py-2.5 bg-orange-700 hover:bg-orange-800 text-white rounded-lg font-medium transition shadow-sm text-sm"
              >
                End Interview
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default InterviewPage;
